import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Search, Plus, Eye, Pause, Play, Filter, MoreVertical, MapPin,
  Users, Mail, Phone, Calendar, CreditCard, ArrowUpRight, X, Send, RefreshCw,
  ShieldOff, ShieldCheck, ChevronRight, Star, AlertTriangle, CheckCircle,
  MessageSquareText, Wallet, BarChart2, Loader2, Gift, History
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import api from '../../api';
import { MASTER } from '../../constants/testIds';

const STATUS_STYLES = {
  active:    'bg-emerald-50 text-emerald-700 border border-emerald-200',
  trial:     'bg-amber-50 text-amber-700 border border-amber-200',
  suspended: 'bg-red-50 text-red-700 border border-red-200',
  expired:   'bg-slate-100 text-slate-500 border border-slate-200',
};

const fmt = (n) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(n || 0);

export default function SchoolsPage() {
  const [schools, setSchools] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [plans, setPlans] = useState([]);

  const loadSchools = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/master/schools?search=${search}&status=${statusFilter === 'all' ? '' : statusFilter}`);
      setSchools(res.data || []);
      setTotal(res.total || 0);
    } catch { toast.error('Failed to load schools'); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { loadSchools(); }, [loadSchools]);
  useEffect(() => { api.get('/master/plans').then(r => setPlans(r.data || [])).catch(() => {}); }, []);

  const handleStatusToggle = async (school) => {
    const action = school.status === 'suspended' ? 'activate' : 'suspend';
    try {
      await api.post(`/master/schools/${school.id}/${action}`);
      toast.success(`School ${action}d`);
      if (selectedSchool?.id === school.id) setSelectedSchool(s => ({ ...s, status: action === 'activate' ? 'active' : 'suspended' }));
      loadSchools();
    } catch { toast.error('Action failed'); }
  };

  const handleUpgradePlan = async (schoolId, planId) => {
    try {
      await api.post(`/master/schools/${schoolId}/assign-plan`, { plan_id: planId });
      toast.success('Plan upgraded successfully');
      const res = await api.get(`/master/schools/${schoolId}`);
      setSelectedSchool(res.data);
      loadSchools();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to upgrade plan'); }
  };

  const handleExtend = async (schoolId, days) => {
    try {
      const res = await api.post(`/master/schools/${schoolId}/extend-subscription`, { days });
      toast.success(res.message || `Extended by ${days} days`);
      const r = await api.get(`/master/schools/${schoolId}`);
      setSelectedSchool(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to extend'); }
  };

  return (
    <div className="flex h-full" data-testid="schools-page">
      {/* Left: School List */}
      <div className={`flex flex-col min-h-0 ${selectedSchool ? 'hidden lg:flex lg:w-[55%]' : 'w-full'} border-r border-slate-100`}>
        <div className="p-6 pb-4 space-y-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-outfit text-xl font-bold text-slate-900">Schools</h1>
              <p className="text-slate-500 text-xs mt-0.5">{total} registered on platform</p>
            </div>
            <Button onClick={() => setShowAddModal(true)} className="h-9 bg-[#516EFA] hover:bg-[#4058e8] text-white text-sm" data-testid={MASTER.addSchoolBtn}>
              <Plus className="w-4 h-4 mr-1.5" />Add School
            </Button>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search schools..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-sm" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 h-9 text-sm"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-y-auto flex-1" data-testid={MASTER.schoolsList}>
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="px-6 py-4 flex gap-3 animate-pulse border-b border-slate-50">
                <div className="w-10 h-10 bg-slate-200 rounded-xl flex-shrink-0" />
                <div className="flex-1 space-y-2 py-1"><div className="h-3.5 bg-slate-200 rounded w-1/2" /><div className="h-3 bg-slate-200 rounded w-1/3" /></div>
              </div>
            ))
          ) : schools.length === 0 ? (
            <div className="py-16 text-center px-6">
              <Building2 className="w-9 h-9 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-600 text-sm">No schools found</p>
              <p className="text-xs text-slate-400 mt-1">Try changing your search or filter</p>
            </div>
          ) : schools.map(school => (
            <div
              key={school.id}
              className={`px-6 py-4 flex items-center justify-between cursor-pointer border-b border-slate-50 hover:bg-slate-50 transition-colors ${selectedSchool?.id === school.id ? 'bg-[#eef1ff] border-l-2 border-l-[#516EFA]' : ''}`}
              onClick={() => setSelectedSchool(school)}
              data-testid={MASTER.schoolRow}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-[#eef1ff] flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-[#516EFA]" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{school.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="text-[11px] text-slate-400 truncate">{school.city}, {school.state}</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-[11px] text-slate-400 capitalize">{school.subscription_plan || 'trial'}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                <span className="text-[11px] text-slate-400">{school.student_count || 0} std</span>
                <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_STYLES[school.status] || ''}`}>{school.status}</Badge>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: School Detail Panel */}
      {selectedSchool ? (
        <div className={`flex flex-col ${selectedSchool ? 'w-full lg:w-[45%]' : 'hidden'} bg-white`}>
          <SchoolDetailPanel
            school={selectedSchool}
            plans={plans}
            onClose={() => setSelectedSchool(null)}
            onStatusToggle={handleStatusToggle}
            onUpgradePlan={handleUpgradePlan}
            onExtend={handleExtend}
          />
        </div>
      ) : (
        <div className="hidden lg:flex flex-1 items-center justify-center bg-slate-50/50">
          <div className="text-center">
            <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm font-medium text-slate-500">Select a school to view details</p>
          </div>
        </div>
      )}

      <AddSchoolModal open={showAddModal} onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); loadSchools(); }} />
    </div>
  );
}

// ─── School Detail Panel ───────────────────────────────────────────────────────

function SchoolDetailPanel({ school: initialSchool, plans, onClose, onStatusToggle, onUpgradePlan, onExtend }) {
  const [tab, setTab] = useState('overview');
  const [school, setSchool] = useState(initialSchool);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [extendDays, setExtendDays] = useState(30);
  const [selectedPlanId, setSelectedPlanId] = useState(initialSchool.plan_id || '');
  const [msgForm, setMsgForm] = useState({ subject: '', message: '' });
  const [sendingMsg, setSendingMsg] = useState(false);
  const [grantForm, setGrantForm] = useState({ credits: '', reason: '' });
  const [granting, setGranting] = useState(false);

  // Load full analytics when panel opens
  useEffect(() => {
    setSchool(initialSchool);
    setSelectedPlanId(initialSchool.plan_id || '');
    setLoadingDetail(true);
    api.get(`/master/schools/${initialSchool.id}`)
      .then(r => { if (r?.data) setSchool(r.data); })
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  }, [initialSchool.id]);

  const expiry = school.subscription_expires_at;
  const expiryDate = expiry ? new Date(expiry).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set';
  const isExpiringSoon = expiry && new Date(expiry) < new Date(Date.now() + 30 * 24 * 3600 * 1000);

  const handleSendMessage = async () => {
    if (!msgForm.subject || !msgForm.message) { toast.error('Subject and message required'); return; }
    setSendingMsg(true);
    try {
      await api.post(`/master/schools/${school.id}/send-message`, msgForm);
      toast.success('Message logged');
      setMsgOpen(false);
      setMsgForm({ subject: '', message: '' });
    } catch { toast.error('Failed to send'); }
    finally { setSendingMsg(false); }
  };

  const handleGrantCredits = async () => {
    const credits = parseInt(grantForm.credits);
    if (!credits || credits <= 0) { toast.error('Enter a valid credit amount'); return; }
    if (!grantForm.reason.trim()) { toast.error('Reason is required'); return; }
    setGranting(true);
    try {
      const r = await api.post(`/master/schools/${school.id}/grant-sms-credits`, { credits, reason: grantForm.reason });
      toast.success(r.message || `${credits} credits granted`);
      setGrantForm({ credits: '', reason: '' });
      // Refresh school data
      api.get(`/master/schools/${school.id}`).then(res => { if (res?.data) setSchool(res.data); }).catch(() => {});
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to grant credits'); }
    finally { setGranting(false); }
  };

  const TABS = ['overview', 'analytics', 'subscription', 'actions'];

  return (
    <div className="flex flex-col h-full" data-testid="school-detail-panel">
      {/* Panel Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#eef1ff] flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#516EFA]" />
          </div>
          <div>
            <p className="font-outfit font-bold text-slate-900">{school.name}</p>
            <div className="flex items-center gap-2">
              <Badge className={`text-[10px] ${STATUS_STYLES[school.status] || ''}`}>{school.status}</Badge>
              <span className="text-[11px] text-slate-400 capitalize">{school.subscription_plan || 'trial'}</span>
              {loadingDetail && <RefreshCw className="w-3 h-3 text-slate-400 animate-spin" />}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-100 flex-shrink-0 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-xs font-semibold capitalize transition-colors whitespace-nowrap ${tab === t ? 'text-[#516EFA] border-b-2 border-[#516EFA]' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`school-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="overflow-y-auto flex-1 p-6 space-y-5">
        {tab === 'overview' && (
          <>
            {/* Contact Info */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Contact Information</p>
              {[
                { icon: Mail, label: school.email },
                { icon: Phone, label: school.phone || 'No phone' },
                { icon: MapPin, label: `${school.city}, ${school.state}, ${school.country}` },
              ].map((row, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <row.icon className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                  <span className="text-sm text-slate-700">{row.label}</span>
                </div>
              ))}
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Students', value: school.student_count ?? 0, color: '#516EFA' },
                { label: 'Teachers', value: school.teacher_count ?? 0, color: '#14C8C4' },
                { label: 'Parents', value: school.parent_count ?? 0, color: '#8B5CF6' },
              ].map((s, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3 text-center" data-testid={`school-stat-${s.label.toLowerCase()}`}>
                  <p className="font-outfit font-bold text-lg" style={{ color: s.color }}>{s.value.toLocaleString()}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* School info */}
            <div className="space-y-2 text-sm">
              {[
                { label: 'School ID', value: school.id?.slice(0, 8) + '...' },
                { label: 'Slug', value: school.slug || '—' },
                { label: 'Registered', value: school.created_at ? new Date(school.created_at).toLocaleDateString('en-NG') : '—' },
                { label: 'Last updated', value: school.updated_at ? new Date(school.updated_at).toLocaleDateString('en-NG') : '—' },
              ].map((r, i) => (
                <div key={i} className="flex justify-between py-1.5 border-b border-slate-50">
                  <span className="text-slate-500 text-xs">{r.label}</span>
                  <span className="text-slate-800 text-xs font-medium">{r.value}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'analytics' && (
          <>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">SMS Platform</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="w-4 h-4 text-blue-500" />
                  <p className="text-xs font-semibold text-blue-600">SMS Credits</p>
                </div>
                <p className="text-2xl font-extrabold text-blue-700" data-testid="school-sms-balance">{(school.sms_wallet_balance ?? 0).toLocaleString()}</p>
                <p className="text-[11px] text-blue-400">available balance</p>
              </div>
              <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquareText className="w-4 h-4 text-violet-500" />
                  <p className="text-xs font-semibold text-violet-600">Campaigns</p>
                </div>
                <p className="text-2xl font-extrabold text-violet-700" data-testid="school-campaigns-count">{(school.total_campaigns ?? 0).toLocaleString()}</p>
                <p className="text-[11px] text-violet-400">total sent</p>
              </div>
            </div>

            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mt-2">Subscription</p>
            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-xl p-4 border ${isExpiringSoon ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-100'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className={`w-4 h-4 ${isExpiringSoon ? 'text-amber-500' : 'text-emerald-500'}`} />
                  <p className={`text-xs font-semibold ${isExpiringSoon ? 'text-amber-600' : 'text-emerald-600'}`}>Expires</p>
                </div>
                <p className={`text-sm font-bold ${isExpiringSoon ? 'text-amber-700' : 'text-emerald-700'}`}>{expiryDate}</p>
              </div>
              <div className={`rounded-xl p-4 border ${(school.days_remaining ?? 99) <= 7 ? 'bg-red-50 border-red-100' : (school.days_remaining ?? 99) <= 30 ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <BarChart2 className="w-4 h-4 text-slate-500" />
                  <p className="text-xs font-semibold text-slate-600">Days Left</p>
                </div>
                <p className={`text-2xl font-extrabold ${(school.days_remaining ?? 99) <= 7 ? 'text-red-600' : (school.days_remaining ?? 99) <= 30 ? 'text-amber-600' : 'text-slate-700'}`} data-testid="school-days-remaining">
                  {school.days_remaining ?? '—'}
                </p>
              </div>
            </div>

            {/* Free SMS Credit Grant */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-emerald-500" />
                <p className="text-xs font-bold text-slate-700">Grant Free SMS Credits</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Credits</Label>
                  <Input type="number" min="1" value={grantForm.credits} onChange={e => setGrantForm(f => ({...f, credits: e.target.value}))} placeholder="e.g. 500" className="h-9 text-sm" data-testid="grant-credits-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Reason</Label>
                  <Input value={grantForm.reason} onChange={e => setGrantForm(f => ({...f, reason: e.target.value}))} placeholder="e.g. Goodwill" className="h-9 text-sm" data-testid="grant-reason-input" />
                </div>
              </div>
              <Button onClick={handleGrantCredits} disabled={granting || !grantForm.credits || !grantForm.reason} className="w-full h-9 bg-emerald-500 hover:bg-emerald-600 text-white text-sm" data-testid="grant-credits-btn">
                {granting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Gift className="w-3.5 h-3.5 mr-1.5" />}
                {granting ? 'Granting...' : 'Grant Credits'}
              </Button>
            </div>
          </>
        )}

        {tab === 'subscription' && (
          <>
            {/* Subscription Status */}
            <div className={`rounded-xl p-4 border ${isExpiringSoon ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start gap-3">
                <Calendar className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isExpiringSoon ? 'text-amber-600' : 'text-slate-500'}`} />
                <div>
                  <p className={`text-xs font-semibold ${isExpiringSoon ? 'text-amber-700' : 'text-slate-600'}`}>Subscription Expiry</p>
                  <p className={`font-semibold mt-0.5 ${isExpiringSoon ? 'text-amber-800' : 'text-slate-800'}`}>{expiryDate}</p>
                  {school.days_remaining !== null && school.days_remaining !== undefined && (
                    <p className={`text-[11px] mt-1 ${(school.days_remaining ?? 99) <= 7 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                      {school.days_remaining === 0 ? 'Expired!' : `${school.days_remaining} day(s) remaining`}
                    </p>
                  )}
                  {isExpiringSoon && <p className="text-[11px] text-amber-600 mt-1">Expiring soon — consider extending</p>}
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              {[
                { label: 'Current Plan', value: school.subscription_plan || 'Trial' },
                { label: 'Plan ID', value: school.plan_id ? school.plan_id.slice(0, 8) + '...' : 'Not assigned' },
                { label: 'Billing Cycle', value: school.billing_cycle || 'Monthly' },
                { label: 'Status', value: school.status },
              ].map((r, i) => (
                <div key={i} className="flex justify-between py-2 border-b border-slate-50">
                  <span className="text-slate-500 text-xs">{r.label}</span>
                  <span className="text-slate-800 text-xs font-semibold capitalize">{r.value}</span>
                </div>
              ))}
            </div>

            {/* Upgrade Plan */}
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Upgrade / Change Plan</p>
              <div className="flex gap-2">
                <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                  <SelectTrigger className="flex-1 h-9 text-sm"><SelectValue placeholder="Select plan..." /></SelectTrigger>
                  <SelectContent>
                    {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — ₦{p.monthly_price?.toLocaleString()}/mo</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button className="h-9 bg-[#516EFA] text-white text-sm" onClick={() => onUpgradePlan(school.id, selectedPlanId)} disabled={!selectedPlanId} data-testid="upgrade-plan-btn">
                  <ArrowUpRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Extend Subscription */}
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Extend Subscription</p>
              <div className="flex gap-2">
                <Select value={String(extendDays)} onValueChange={v => setExtendDays(Number(v))}>
                  <SelectTrigger className="flex-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[7, 14, 30, 60, 90, 180, 365].map(d => <SelectItem key={d} value={String(d)}>+{d} days</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" className="h-9 border-[#14C8C4] text-[#14C8C4] hover:bg-teal-50 text-sm" onClick={() => onExtend(school.id, extendDays)} data-testid="extend-sub-btn">
                  <RefreshCw className="w-4 h-4 mr-1.5" />Extend
                </Button>
              </div>
            </div>

            {/* Subscription History */}
            {(school.subscription_history || []).length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <History className="w-3.5 h-3.5 text-slate-400" />
                  <p className="text-xs font-semibold text-slate-600">Subscription History</p>
                </div>
                <div className="space-y-2">
                  {(school.subscription_history || []).map((sub, i) => (
                    <div key={sub.id || i} className="flex items-center justify-between py-2 border-b border-slate-50 text-xs">
                      <div>
                        <p className="font-medium text-slate-700">{sub.plan_name}</p>
                        <p className="text-slate-400 mt-0.5">{sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-NG') : '—'} · {sub.gateway || '—'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-700">{sub.amount ? `₦${Number(sub.amount).toLocaleString()}` : '—'}</p>
                        <Badge className={`text-[9px] mt-0.5 ${sub.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{sub.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'actions' && (
          <div className="space-y-3">
            {/* Contact */}
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-600 mb-3">Contact School Admin</p>
              {!msgOpen ? (
                <Button variant="outline" className="w-full h-9 text-sm" onClick={() => setMsgOpen(true)} data-testid="contact-school-btn">
                  <Send className="w-4 h-4 mr-2" />Send Message
                </Button>
              ) : (
                <div className="space-y-3">
                  <Input placeholder="Subject" value={msgForm.subject} onChange={e => setMsgForm(f => ({ ...f, subject: e.target.value }))} className="h-9 text-sm" />
                  <Textarea placeholder="Message..." value={msgForm.message} onChange={e => setMsgForm(f => ({ ...f, message: e.target.value }))} rows={3} className="text-sm" />
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1 h-8 text-xs" onClick={() => setMsgOpen(false)}>Cancel</Button>
                    <Button className="flex-1 h-8 text-xs bg-[#516EFA] text-white" onClick={handleSendMessage} disabled={sendingMsg}>
                      {sendingMsg ? 'Sending...' : 'Send'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Disable / Enable */}
            <div className={`rounded-xl p-4 border ${school.status === 'suspended' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-100'}`}>
              <p className="text-xs font-semibold mb-1.5 text-slate-600">
                {school.status === 'suspended' ? 'Enable School Access' : 'Disable School Access'}
              </p>
              <p className="text-[11px] text-slate-500 mb-3">
                {school.status === 'suspended'
                  ? 'Re-enable this school so their staff and students can log in.'
                  : 'Suspend this school — all users will be blocked from logging in.'}
              </p>
              <Button
                className={`w-full h-9 text-sm ${school.status === 'suspended' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                onClick={() => onStatusToggle(school)}
                data-testid="toggle-status-btn"
              >
                {school.status === 'suspended'
                  ? <><ShieldCheck className="w-4 h-4 mr-2" />Enable School</>
                  : <><ShieldOff className="w-4 h-4 mr-2" />Disable School</>
                }
              </Button>
            </div>

            {/* Notes */}
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-600 mb-2">Admin Notes</p>
              <Textarea placeholder="Add internal notes about this school..." rows={3} className="text-sm bg-white" defaultValue={school.notes || ''} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add School Modal ──────────────────────────────────────────────────────────

function AddSchoolModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', school_type: 'secondary', email: '', phone: '', state: 'Lagos', city: '', subscription_plan: 'trial', status: 'active' });
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    setLoading(true);
    try {
      await api.post('/master/schools', form);
      toast.success('School created successfully');
      onSuccess();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create school'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-outfit">Add New School</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          {[{ label: 'School Name', key: 'name', placeholder: 'Greenfield Academy' }, { label: 'Email', key: 'email', placeholder: 'info@school.edu.ng' }, { label: 'Phone', key: 'phone', placeholder: '+234 801 234 5678' }, { label: 'City', key: 'city', placeholder: 'Lagos Island' }].map(f => (
            <div key={f.key}>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{f.label}</Label>
              <Input value={form[f.key]} onChange={e => update(f.key, e.target.value)} placeholder={f.placeholder} className="h-10" />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Type</Label>
              <Select value={form.school_type} onValueChange={v => update('school_type', v)}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{['nursery', 'primary', 'secondary'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Plan</Label>
              <Select value={form.subscription_plan} onValueChange={v => update('subscription_plan', v)}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{['trial', 'starter', 'professional', 'enterprise'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#516EFA] hover:bg-[#4058e8] text-white">
              {loading ? 'Creating...' : 'Create School'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
